"use client";

import React from "react";

import {Slot} from "@radix-ui/react-slot";
import {input} from "@styled-system/recipes/input";
import {ChevronDown, ChevronUp} from "lucide-react";

import type {InputVariantProps} from "@styled-system/recipes";
import type {JsxStyleProps} from "@styled-system/types";

import {css, cx} from "@styled-system/css";

// 1. Context stores all props (as a generic object)
const InputContext = React.createContext<Record<string, any> | null>(null);

type InputRootProps = React.HTMLAttributes<HTMLDivElement> & InputVariantProps & JsxStyleProps;
// Root component
const InputRoot = React.forwardRef<HTMLDivElement, InputRootProps>(
    ({className, size, css: cssProp, shape, color, fontStyle, children, ...props}, ref) => {
        const id = React.useId();
        const {root} = input({size, shape, color, fontStyle, ...props});

        return (
            <InputContext.Provider value={{id, size, shape, color, fontStyle}}>
                <div ref={ref} className={cx(root, cssProp && css(cssProp), className)}>
                    {children}
                </div>
            </InputContext.Provider>
        );
    }
);
InputRoot.displayName = "Input";

// Prefix component
const InputPrefix = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({className, ...props}, ref) => {
        const context = React.useContext(InputContext) || {};
        const {id, ...inputProps} = context;
        const {prefix} = input(inputProps);

        return <div ref={ref} className={cx(prefix, className)} {...props} />;
    }
);
InputPrefix.displayName = "Input.Prefix";

// Postfix component
const InputPostfix = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({className, ...props}, ref) => {
        const context = React.useContext(InputContext) || {};
        const {id, ...inputProps} = context;
        const {postfix} = input(inputProps);

        return <div ref={ref} className={cx(postfix, className)} {...props} />;
    }
);
InputPostfix.displayName = "Input.Postfix";

export type InputTextProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
    formatter?: (value: string) => string;
    maxLength?: number;
    showCount?: boolean;
    status?: "error" | "success" | "warning";
} & Omit<InputVariantProps, "size">;

// Text input component
const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
    ({className, formatter, maxLength, showCount, status, onChange, value, defaultValue, ...props}, ref) => {
        const context = React.useContext(InputContext) || {};
        const {id, ...inputProps} = context;
        const {field, charCount} = input(inputProps);
        const [inputValue, setInputValue] = React.useState(value || defaultValue || "");
        const characterCount = String(inputValue).length;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let newValue = e.target.value;
            if (formatter) {
                newValue = formatter(newValue);
            }
            if (maxLength) {
                newValue = newValue.slice(0, maxLength);
            }
            setInputValue(newValue);
            onChange?.(e);
        };

        return (
            <div className={css({position: "relative", width: "100%"})}>
                <Slot className={css({flexGrow: 1})}>
                    <input
                        id={id}
                        ref={ref}
                        type="text"
                        value={value ?? inputValue}
                        onChange={handleChange}
                        maxLength={maxLength}
                        className={cx(field, className)}
                        data-status={status}
                        {...props}
                    />
                </Slot>
                {showCount && maxLength && (
                    <div className={charCount}>
                        {characterCount}/{maxLength}
                    </div>
                )}
            </div>
        );
    }
);
InputText.displayName = "Input.Text";

type InputNumberProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & {
    controls?: boolean;
    step?: number;
    min?: number;
    max?: number;
};

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
    ({className, controls = true, step = 1, min, max, value, onChange, ...props}, ref) => {
        const context = React.useContext(InputContext) || {};
        const {id, ...inputProps} = context;
        const {field, control} = input(inputProps);
        const [localValue, setLocalValue] = React.useState<number | undefined>(
            value !== undefined ? Number(value) : undefined
        );

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value === "" ? undefined : Number(e.target.value);
            setLocalValue(newValue);
            onChange?.(e);
        };

        const increment = () => {
            if (localValue === undefined) {
                const newValue = min ?? 0;
                setLocalValue(newValue);
                simulateInputChange(newValue);
            } else {
                const newValue = Math.min(max ?? Number.POSITIVE_INFINITY, localValue + step);
                setLocalValue(newValue);
                simulateInputChange(newValue);
            }
        };

        const decrement = () => {
            if (localValue === undefined) {
                const newValue = min ?? 0;
                setLocalValue(newValue);
                simulateInputChange(newValue);
            } else {
                const newValue = Math.max(min ?? Number.NEGATIVE_INFINITY, localValue - step);
                setLocalValue(newValue);
                simulateInputChange(newValue);
            }
        };

        // Helper function to simulate input change event
        const simulateInputChange = (newValue: number) => {
            const event = {
                target: {value: String(newValue)}
            } as React.ChangeEvent<HTMLInputElement>;
            onChange?.(event);
        };

        return (
            <div className={css({display: "flex", alignItems: "center", width: "100%"})}>
                <Slot className={css({flexGrow: 1, position: "relative"})}>
                    <input
                        id={id}
                        ref={ref}
                        type="number"
                        value={value ?? localValue ?? ""}
                        onChange={handleChange}
                        min={min}
                        max={max}
                        step={step}
                        className={cx(
                            field,
                            css({
                                // Hide browser default spinners
                                WebkitAppearance: "textfield",
                                appearance: "textfield"
                            }),
                            className
                        )}
                        {...props}
                    />
                </Slot>
                {controls && (
                    <div
                        className={css({
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.125rem"
                        })}>
                        <button
                            type="button"
                            onClick={increment}
                            disabled={localValue !== undefined && localValue >= (max ?? Number.POSITIVE_INFINITY)}
                            className={control}>
                            <ChevronUp size={14} />
                        </button>
                        <button
                            type="button"
                            onClick={decrement}
                            disabled={localValue !== undefined && localValue <= (min ?? Number.NEGATIVE_INFINITY)}
                            className={control}>
                            <ChevronDown size={14} />
                        </button>
                    </div>
                )}
            </div>
        );
    }
);
InputNumber.displayName = "Input.Number";

// Update the Input export
export const Input = Object.assign(InputRoot, {
    Prefix: InputPrefix,
    Postfix: InputPostfix,
    Text: InputText,
    Number: InputNumber
});
